import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useVault } from '../session/useVault';
import { VaultErrorSection } from '../session/VaultErrorSection';
import { resolveId } from '../entity/catalog';
import { hrefFor } from './EntityPage';
import { Loading } from '../shell/Loading';

/**
 * `/o/<id>` — the one outbound link form, whatever it points at.
 *
 * A link is written by something that has an id and often does not know what
 * kind of thing it is: a session writing into an artifact, a calendar event, an
 * arc citing a recording. So the kind is resolved on arrival rather than
 * guessed at the point of writing, where a wrong guess is a dead link.
 *
 * The id is a POINTER, not a capability. It carries no key and says nothing
 * about the content, which is what makes the form safe to paste into a calendar
 * invite or a shared page — anyone without the vault gets an offer to connect
 * and nothing else. That property is the reason ids rather than names are what
 * gets written down: a name would publish the subject to every browser history
 * and referrer the link ever passes through.
 */
export function OpenRoute() {
  const { openId } = useParams();
  const { state, reload } = useVault();
  const id = (openId ?? '').trim();
  const [target, setTarget] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (state.status !== 'ready' || !id) return;
    let cancelled = false;
    setMissing(false);
    resolveId(state.vault, id)
      .then((entry) => {
        if (cancelled) return;
        if (entry) setTarget(hrefFor(entry.path));
        else setMissing(true);
      })
      .catch(() => !cancelled && setMissing(true));
    return () => {
      cancelled = true;
    };
  }, [state, id]);

  if (target) return <Navigate to={target} replace />;

  // Never a 404 for someone without the vault: on a phone this URL would have
  // opened the app, so reaching the web at all usually means the reader is
  // somewhere else — and "not found" would say the thing is gone rather than
  // that this browser cannot see it.
  if (state.status === 'disconnected') {
    return (
      <section className="placeholder">
        <h1>This is a Pondering link.</h1>
        <p className="t-bodyProse">
          It points at one thing in a vault only its owner can open. On a phone with the app it
          opens there.
        </p>
        <p>
          <Link to="/connect">Connect this browser</Link> if the vault is yours.
        </p>
      </section>
    );
  }

  if (state.status === 'error') return <VaultErrorSection state={state} reload={reload} />;

  if (missing) {
    return (
      <section className="placeholder">
        <h1>Not in this vault</h1>
        <p className="t-bodyProse">
          Nothing here carries that id. It may belong to a different vault, or predate the catalog.
        </p>
        <p className="t-metaMono placeholder-phase">{id}</p>
        <p>
          <Link to="/plan">Back to the board</Link>
        </p>
      </section>
    );
  }

  return <Loading title="Opening…" />;
}
